import React from "react";
import { Link } from "react-router-dom";

import { EventLink, ProfileLink } from "app/links";
import { Trans } from "app/utils";

import UserName from "components/common/UserName";
import CommentBody from "components/comments/CommentBody";
import { TimeDiff } from "components/common/Time";
import LazyEditorOutput from "components/editor/LazyEditorOutput";

import FlagActions from "./FlagActions";

const EventPreview = ({ event }) => (
  <div>
    <div className="AdminFlags__row__description">
      <UserName user={event.host} />&#39;s hosted event:
      {" "}
      <EventLink event={event} />
    </div>
    <img
      className="AdminFlags__row__image"
      src={event.image.mediumUrl}
      alt={event.name}
    />
  </div>
);

const PhotoPreview = ({ photo: { uploader, event, image } }) => (
  <div>
    <div className="AdminFlags__row__description">
      <UserName user={uploader} />&#39;s photo on the event{" "}
      <Link to={`/events/${event.id}/photos`}>
        {event.name}
      </Link>
    </div>
    <img
      className="AdminFlags__row__image"
      src={image.mediumUrl}
      alt={uploader.name}
    />
  </div>
);

const CommentPreview = ({ comment }) => {
  const { commentable = {}, user = {} } = comment;
  const { entity = {} } = commentable;

  // Is the comment on an event?
  const isEvent = commentable.activityType === "event_created";

  // Is the comment on a recognition?
  const isRecognition = commentable.organizationValues;

  return (
    <div>
      <div className="AdminFlags__row__description">
        <UserName user={user} />
        <span>&#39;s comment</span>
        {isEvent && (
          <>
            {" "}
            on the event <EventLink event={entity} />
          </>
        )}

        {isRecognition && (
          <>
            {" "}
            on the recognition
            {commentable.body}
            {" "}
            {commentable.creator && <>by {commentable.creator.name}</>}
          </>
        )}
      </div>
      <div className="AdminFlags__row__quote">
        <CommentBody comment={comment} />
      </div>
    </div>
  );
};

const RecognitionPreview = ({ recognition }) => (
  <div>
    <div className="AdminFlags__row__description">
      {recognition.creator ? (
        <>
          <UserName user={recognition.creator} />&#39;s
        </>
      ) : (
        "The "
      )}
      <span> recognition of </span>
      <UserName user={recognition.users[0]} />
    </div>
    <div className="AdminFlags__row__quote">
      <p>
        <i><LazyEditorOutput output={recognition.body} /></i>
      </p>
    </div>
  </div>
);

const UserPreview = ({ user }) => (
  <div className="AdminFlags__row__description">
    <strong>User:</strong> <ProfileLink user={user} />
  </div>
);

const StoryPreview = ({ story }) => (
  <div>
    <div className="AdminFlags__row__description">
      <UserName user={story.creator} />&#39;s answered story:
      {" "}
      {story.question.question}
    </div>
    <img
      className="AdminFlags__row__image"
      src={story.media.url}
      alt={story.question.question}
    />
  </div>
);

const PostPreview = ({ post }) => (
  <div>
    <div className="AdminFlags__row__description">
      {post.creator ? (
        <>
          <UserName user={post.creator} />&#39;s
        </>
      ) : (
        "The "
      )}
      <span> post</span>
    </div>
    <div className="AdminFlags__row__quote">
      <p>
        <i><LazyEditorOutput output={post.body} /></i>
      </p>
    </div>
  </div>
);

const DefaultPreview = ({ data, type }) => {
  const keys = {
    Company: "companyId",
    Department: "departmentId",
    Interest: "interestId",
    Location: "locationId"
  };

  const key = keys[type];

  return (
    <div>
      <div className="AdminFlags__row__description">
        <strong>{type}: </strong>{" "}
        {key && <Link to={`/community?${key}=${data.id}`}>{data.name}</Link>}
        {!key && <span>{data.name}</span>}
      </div>
    </div>
  );
};

const PreviewContent = ({ type, flaggable }) => {
  switch (type) {
    case "Event":
      return <EventPreview event={flaggable} />;
    case "Photo":
      return <PhotoPreview photo={flaggable} />;
    case "Comment":
      return <CommentPreview comment={flaggable} />;
    case "Recognition":
      return <RecognitionPreview recognition={flaggable} />;
    case "User":
      return <UserPreview user={flaggable} />;
    case "Post":
      return <PostPreview post={flaggable} />;
    case "Story":
      return <StoryPreview story={flaggable} />;
    default:
      return <DefaultPreview data={flaggable} type={type} />;
  }
};

const FlagRow = ({ flag, isLast, onToggleReviewed, onDeleted }) => (
  <div className="AdminFlags__row">
    <div className="row">
      <div className="col-sm-12">
        <div className="col-sm-6">
          <PreviewContent type={flag.flaggableType} flaggable={flag.flaggable} />

          {flag.message && (
            <div className="AdminFlags__row__note">
              <strong><Trans>Note from {flag.flagger.name}:</Trans></strong>
              {" "}
              {flag.message}
            </div>
          )}
        </div>
        <div className="col-sm-3">
          <UserName user={flag.flagger} />
          <br />
          <p className="date">
            Flagged <TimeDiff time={flag.createdAt} />
          </p>
        </div>
        <div className="col-sm-3">
          <FlagActions
            flag={flag}
            onToggleReviewed={onToggleReviewed}
            onDeleted={onDeleted}
          />
        </div>
      </div>
    </div>
    {!isLast && <hr />}
  </div>
);

export default FlagRow;
